import { Color, CustomBlending, HalfFloatType, LinearSRGBColorSpace, ACESFilmicToneMapping, NoToneMapping, OneFactor, PerspectiveCamera, Vector2, Vector4, WebGLRenderTarget, ZeroFactor } from "three";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { SMAAPass } from "three/examples/jsm/postprocessing/SMAAPass.js";
import { applySmaaQuality } from "./SmaaSettings";
import { AmbientOcclusionRenderer } from "./AmbientOcclusionRenderer";
import { getBloomSettings } from "./BloomSettings";
import { Layer } from "../RenderOrder";
const DEFAULT_BLOOM_SETTINGS = getBloomSettings();
export class ScenePostprocessingRenderer {
    constructor(renderer) {
        this.renderer = renderer;
        this.viewCamera = new PerspectiveCamera();
        this.viewport = new Vector4();
        this.logicalViewport = new Vector4();
        this.targetViewport = new Vector4();
        this.clearColor = new Color();
        this.smaaClearColor = new Color();
        this.ambientOcclusion = new AmbientOcclusionRenderer(renderer);
    }
    render(scene, camera, usePostEffects = false, gtaoSettings, bloomSettings = DEFAULT_BLOOM_SETTINGS, bloomResolutionScale = 0.25, smaaQuality = "medium", useBloom = true, useGtao = true) {
        if (!usePostEffects) {
            this.disposeBloom();
            this.ambientOcclusion.dispose();
            const cameraLayers = camera.layers.mask;
            const toneMapping = this.renderer.toneMapping;
            if (toneMapping === NoToneMapping)
                this.renderer.toneMapping = ACESFilmicToneMapping;
            camera.layers.enable(Layer.POSTPROCESSING_OVERLAY);
            try {
                this.renderer.render(scene, camera);
            }
            finally {
                camera.layers.mask = cameraLayers;
                this.renderer.toneMapping = toneMapping;
            }
            return;
        }
        if (!useBloom) {
            this.disposeBloom();
        }
        const renderer = this.renderer;
        const target = renderer.getRenderTarget();
        const xrEnabled = renderer.xr.enabled;
        const autoClear = renderer.autoClear;
        const toneMapping = renderer.toneMapping;
        const outputColorSpace = renderer.outputColorSpace;
        const clearAlpha = renderer.getClearAlpha();
        renderer.getClearColor(this.clearColor);
        renderer.getCurrentViewport(this.viewport);
        renderer.getViewport(this.logicalViewport);
        if (target)
            this.targetViewport.copy(target.viewport);
        try {
            if (scene.matrixWorldAutoUpdate)
                scene.updateMatrixWorld();
            if (camera.parent === null && camera.matrixWorldAutoUpdate)
                camera.updateMatrixWorld();
            if (xrEnabled && renderer.xr.isPresenting && renderer.xr.cameraAutoUpdate)
                renderer.xr.updateCamera(camera);
            renderer.xr.enabled = false;
            // Keep scene color linear through GTAO, bloom and SMAA; OutputPass performs the final conversion.
            if (toneMapping === NoToneMapping)
                renderer.toneMapping = ACESFilmicToneMapping;
            if (target && !(xrEnabled && renderer.xr.isPresenting))
                renderer.outputColorSpace = LinearSRGBColorSpace;
            if (xrEnabled && renderer.xr.isPresenting) {
                for (const eye of renderer.xr.getCamera().cameras) {
                    if (!eye.viewport)
                        throw new Error("Postprocessing requires a viewport for each XR eye");
                    this.renderView(scene, eye, eye.viewport, target, gtaoSettings, bloomSettings, bloomResolutionScale, smaaQuality, useBloom, useGtao);
                }
            }
            else {
                this.renderView(scene, camera, this.viewport, target, gtaoSettings, bloomSettings, bloomResolutionScale, smaaQuality, useBloom, useGtao);
            }
        }
        finally {
            renderer.xr.enabled = xrEnabled;
            renderer.autoClear = autoClear;
            renderer.toneMapping = toneMapping;
            renderer.outputColorSpace = outputColorSpace;
            renderer.setClearColor(this.clearColor, clearAlpha);
            renderer.setViewport(this.logicalViewport);
            if (target)
                target.viewport.copy(this.viewport);
            renderer.setRenderTarget(target);
            if (target)
                target.viewport.copy(this.targetViewport);
        }
    }
    dispose() {
        this.disposeBloom();
        if (this.resources) {
            this.resources.target.dispose();
            this.resources.antialiasedTarget.dispose();
            this.resources.smaa.dispose();
            this.resources.output.dispose();
            this.resources = undefined;
        }
        this.ambientOcclusion.dispose();
    }
    disposeBloom() {
        if (!this.bloom)
            return;
        this.bloom.pass.dispose();
        this.bloom = undefined;
    }
    renderView(scene, camera, viewport, destination, gtaoSettings, bloomSettings, bloomResolutionScale, smaaQuality, useBloom, useGtao) {
        var _a;
        const renderer = this.renderer;
        const width = Math.max(1, Math.floor(viewport.z));
        const height = Math.max(1, Math.floor(viewport.w));
        const bloomWidth = Math.max(32, Math.floor(width * bloomResolutionScale));
        const bloomHeight = Math.max(32, Math.floor(height * bloomResolutionScale));
        if (!this.resources) {
            const target = new WebGLRenderTarget(width, height, {
                type: HalfFloatType,
                colorSpace: LinearSRGBColorSpace,
                samples: 0,
                stencilBuffer: true,
                generateMipmaps: false
            });
            const antialiasedTarget = new WebGLRenderTarget(width, height, {
                type: HalfFloatType,
                colorSpace: LinearSRGBColorSpace,
                samples: 0,
                depthBuffer: false,
                generateMipmaps: false
            });
            const smaa = new SMAAPass();
            smaa.clear = true;
            smaa.setSize(width, height);
            this.resources = { target, antialiasedTarget, smaa, output: new OutputPass() };
        }
        const { target, antialiasedTarget, smaa, output } = this.resources;
        applySmaaQuality(smaa, smaaQuality);
        if (target.width !== width || target.height !== height) {
            target.setSize(width, height);
            antialiasedTarget.setSize(width, height);
            smaa.setSize(width, height);
        }
        if (useBloom && !this.bloom) {
            // Only the bloom mip chain is reduced; scene color, GTAO and output stay full resolution.
            const pass = new UnrealBloomPass(new Vector2(bloomWidth, bloomHeight), bloomSettings.strength, bloomSettings.radius, bloomSettings.threshold);
            const blendMaterial = pass.blendMaterial;
            blendMaterial.blending = CustomBlending;
            blendMaterial.blendSrc = OneFactor;
            blendMaterial.blendDst = OneFactor;
            blendMaterial.blendSrcAlpha = ZeroFactor;
            blendMaterial.blendDstAlpha = OneFactor;
            this.bloom = {
                pass,
                width: bloomWidth,
                height: bloomHeight
            };
        }
        if (this.bloom) {
            const { pass } = this.bloom;
            pass.strength = bloomSettings.strength;
            pass.radius = bloomSettings.radius;
            pass.threshold = bloomSettings.threshold;
            if (this.bloom.width !== bloomWidth || this.bloom.height !== bloomHeight) {
                pass.setSize(bloomWidth, bloomHeight);
                this.bloom.width = bloomWidth;
                this.bloom.height = bloomHeight;
            }
        }
        this.viewCamera.copy(camera, false);
        this.viewCamera.layers.disable(Layer.POSTPROCESSING_OVERLAY);
        this.viewCamera.viewport = undefined;
        this.viewCamera.matrixWorldAutoUpdate = false;
        renderer.setRenderTarget(target);
        renderer.autoClear = true;
        this.ambientOcclusion.render(scene, this.viewCamera, useGtao, gtaoSettings);
        renderer.autoClear = false;
        (_a = this.bloom) === null || _a === void 0 ? void 0 : _a.pass.render(renderer, target, target, 0, false);
        this.viewCamera.layers.set(Layer.POSTPROCESSING_OVERLAY);
        const background = scene.background;
        scene.background = null;
        try {
            renderer.setRenderTarget(target);
            renderer.render(scene, this.viewCamera);
        }
        finally {
            scene.background = background;
        }
        // Include overlays in edge detection without applying GTAO or bloom to them.
        renderer.getClearColor(this.smaaClearColor);
        const smaaClearAlpha = renderer.getClearAlpha();
        renderer.setClearColor(0x000000, 0);
        try {
            smaa.render(renderer, antialiasedTarget, target, 0, false);
        }
        finally {
            renderer.setClearColor(this.smaaClearColor, smaaClearAlpha);
        }
        if (destination) {
            destination.viewport.copy(viewport);
        }
        else {
            renderer.setViewport(viewport.clone().divideScalar(renderer.getPixelRatio()));
        }
        renderer.autoClear = false;
        output.renderToScreen = destination === null;
        output.render(renderer, destination !== null && destination !== void 0 ? destination : antialiasedTarget, antialiasedTarget, 0, false);
    }
}
